import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { Mail, Pause, Play, Trash2, RefreshCw, Settings, ChevronDown } from 'lucide-react';
import { Button } from '@components/common/Button';
import { InfoTooltip } from '@components/common/InfoTooltip';
import { MultiSearchableSelect, SearchableOption } from '@components/common/MultiSearchableSelect';
import { useMailboxFolders, useUpdateMailboxConnection } from '@api/hooks/useMailbox';
import type { MailboxConnection } from '@schemas/api';
import { formatDateTime } from '@utils/formatters';

interface MailboxConnectionCardProps {
  connection: MailboxConnection;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onDelete: (id: string) => void;
  onPollNow: (id: string) => void;
  isPausing?: boolean;
  isResuming?: boolean;
  isDeleting?: boolean;
  isPolling?: boolean;
}

const STATUS_STYLES: Record<string, { dot: string; bg: string; text: string }> = {
  active: {
    dot: 'bg-green-500',
    bg: 'bg-green-50 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300',
  },
  paused: {
    dot: 'bg-yellow-500',
    bg: 'bg-yellow-50 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300',
  },
  error: {
    dot: 'bg-red-500',
    bg: 'bg-red-50 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300',
  },
};

const PROVIDER_LABELS: Record<string, string> = {
  microsoft: 'Microsoft 365',
  gmail: 'Gmail',
  imap: 'IMAP',
};

export const MailboxConnectionCard = ({
  connection,
  onPause,
  onResume,
  onDelete,
  onPollNow,
  isPausing,
  isResuming,
  isDeleting,
  isPolling,
}: MailboxConnectionCardProps) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const style = STATUS_STYLES[connection.status] ?? STATUS_STYLES.error;
  const providerLabel = PROVIDER_LABELS[connection.provider] ?? connection.provider;
  const isActive = connection.status === 'active';
  const isPausedStatus = connection.status === 'paused';

  // Folder selection
  const { data: foldersData, isLoading: foldersLoading } = useMailboxFolders(
    showSettings ? connection.id : null
  );
  const updateMutation = useUpdateMailboxConnection();

  const selectedFolderIds: string[] = connection.filter_rules?.folders ?? [];

  const folderOptions: SearchableOption[] = useMemo(() => {
    if (!foldersData?.folders) return [];
    return foldersData.folders.map((f) => ({
      id: f.id,
      label: f.name,
      description: f.message_count != null ? `${f.message_count} messages` : undefined,
    }));
  }, [foldersData]);

  const handleFolderChange = (folderIds: string[]) => {
    const newFilterRules = {
      ...connection.filter_rules,
      folders: folderIds,
    };
    const newFilterMode = folderIds.length > 0 ? 'filtered' : 'all';

    updateMutation.mutate({
      id: connection.id,
      filter_mode: newFilterMode,
      filter_rules: newFilterRules,
    });
  };

  // Display which folders are currently configured
  const folderSummary = selectedFolderIds.length > 0
    ? `${selectedFolderIds.length} folder${selectedFolderIds.length > 1 ? 's' : ''} selected`
    : 'All folders';

  return (
    <div
      className={clsx(
        'rounded-lg border p-4',
        'border-gray-200 dark:border-gray-700',
        'bg-white dark:bg-gray-800'
      )}
    >
      {/* Header row */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {connection.display_name || connection.email_address}
            </p>
            {connection.display_name && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {connection.email_address}
              </p>
            )}
          </div>
        </div>
        <div
          className={clsx(
            'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
            style.bg,
            style.text
          )}
        >
          <span className={clsx('inline-block h-2 w-2 rounded-full', style.dot)} />
          {connection.status}
        </div>
      </div>

      {/* Details */}
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span>{providerLabel}</span>
        <span>{folderSummary}</span>
        <span>Polls every {connection.polling_interval_minutes}m</span>
        <span>Monitoring from: {formatDateTime(connection.initial_poll_at)}</span>
        {connection.last_polled_at && (
          <span>
            Last polled: {formatDateTime(connection.last_polled_at)}
          </span>
        )}
      </div>

      {/* Error message */}
      {connection.error_message && (
        <p className="mt-2 text-xs text-red-600 dark:text-red-400">
          {connection.error_message}
        </p>
      )}

      {/* Folder settings (expandable) */}
      <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
        <button
          type="button"
          onClick={() => setShowSettings((prev) => !prev)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
        >
          <Settings className="h-3.5 w-3.5" />
          Inbox Settings
          <ChevronDown
            className={clsx(
              'h-3.5 w-3.5 transition-transform',
              showSettings && 'rotate-180'
            )}
          />
        </button>

        {showSettings && (
          <div className="mt-3 space-y-3">
            <div>
              <div className="mb-1.5 flex items-center gap-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Monitor specific folders</label>
                <InfoTooltip>Select folders to fetch invoices from. Leave empty to monitor every folder.</InfoTooltip>
              </div>
              <MultiSearchableSelect
                options={folderOptions}
                value={selectedFolderIds}
                onChange={handleFolderChange}
                placeholder="Search folders..."
                isLoading={foldersLoading}
                emptyMessage="No folders found"
              />
              {updateMutation.isPending && (
                <p className="mt-1 text-xs text-primary-600 dark:text-primary-400">
                  Saving...
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-3 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700 pt-3">
        {isActive && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onPause(connection.id)}
            loading={isPausing}
            disabled={isPausing}
          >
            <Pause className="h-3.5 w-3.5 mr-1" />
            Pause
          </Button>
        )}
        {isPausedStatus && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onResume(connection.id)}
            loading={isResuming}
            disabled={isResuming}
          >
            <Play className="h-3.5 w-3.5 mr-1" />
            Resume
          </Button>
        )}
        {isActive && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onPollNow(connection.id)}
            loading={isPolling}
            disabled={isPolling}
          >
            <RefreshCw className={clsx('h-3.5 w-3.5 mr-1', isPolling && 'animate-spin')} />
            Poll Now
          </Button>
        )}
        <div className="flex-1" />
        {!showConfirmDelete ? (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowConfirmDelete(true)}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Remove
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600 dark:text-red-400">Confirm?</span>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                onDelete(connection.id);
                setShowConfirmDelete(false);
              }}
              loading={isDeleting}
            >
              Yes, remove
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowConfirmDelete(false)}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
